import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Plus, X, Pencil, Trash2 } from "lucide-react";
import { AGENCY_TIERS } from "./constants";

export default function AgenciesTab({ onChange }) {
  const [agencies, setAgencies] = useState([]);
  const [modal, setModal] = useState(null); // true(new) | agency(edit)

  const load = () => api.get("/library/agencies").then((r) => setAgencies(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const refresh = () => { load(); onChange && onChange(); };

  const del = async (a) => {
    if (!window.confirm(`Delete agency "${a.name}"?`)) return;
    await api.delete(`/library/agencies/${a.id}`); toast.success("Agency deleted"); refresh();
  };

  return (
    <div data-testid="agencies-tab">
      <div className="flex items-center justify-end mb-4">
        <button data-testid="add-agency-btn" onClick={() => setModal(true)}
          className="flex items-center gap-1.5 bg-zinc-900 text-white font-semibold text-sm px-3 py-2 rounded-md hover:bg-zinc-800">
          <Plus className="h-4 w-4" /> Agency</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agencies-grid">
        {agencies.length === 0 && <div className="col-span-full text-xs text-zinc-400 py-10 text-center border border-dashed border-zinc-300 rounded-xl">No agencies yet. Add the agencies WD bids to.</div>}
        {agencies.map((a) => (
          <div key={a.id} data-testid={`agency-card-${a.id}`} className="bg-white border border-zinc-200 shadow-sm rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Landmark className="h-4 w-4 text-zinc-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{a.name}</div>
                  <div className="text-[11px] text-zinc-500">{a.abbr}{a.abbr && a.tier ? " · " : ""}{a.tier}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`edit-agency-${a.id}`} onClick={() => setModal(a)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`delete-agency-${a.id}`} onClick={() => del(a)} className="p-1.5 rounded hover:bg-zinc-100 text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            {a.notes && <p className="text-[12px] text-zinc-600 mt-2 line-clamp-3">{a.notes}</p>}
            <div className="text-[11px] text-zinc-400 mt-2">{(a.awards || []).length} award(s)</div>
          </div>
        ))}
      </div>

      {modal && <AgencyModal agency={modal === true ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
    </div>
  );
}

function AgencyModal({ agency, onClose, onSaved }) {
  const [f, setF] = useState({ name: agency?.name || "", abbr: agency?.abbr || "", tier: agency?.tier || "Federal", notes: agency?.notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    if (agency) await api.put(`/library/agencies/${agency.id}`, f);
    else await api.post("/library/agencies", f);
    toast.success(agency ? "Agency updated" : "Agency added"); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-6 space-y-3 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="agency-modal">
        <div className="flex items-center justify-between"><h2 className="font-display font-bold text-xl text-zinc-900">{agency ? "Edit agency" : "New agency"}</h2><button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button></div>
        <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Name</span>
          <input value={f.name} onChange={set("name")} data-testid="agency-name" className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Abbreviation</span>
            <input value={f.abbr} onChange={set("abbr")} data-testid="agency-abbr" className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none" /></label>
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Tier</span>
            <select value={f.tier} onChange={set("tier")} data-testid="agency-tier" className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
              {AGENCY_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
        </div>
        <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Notes</span>
          <textarea value={f.notes} onChange={set("notes")} rows={3} data-testid="agency-notes" className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none resize-none" /></label>
        <button onClick={submit} data-testid="save-agency-btn" className="w-full mt-2 bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800">Save</button>
      </div>
    </div>
  );
}
